import { nameShape } from "@logic/lib/2d"
import type { Node2d } from "@logic/lib/mesh"
import { useRecoilTransaction_UNSTABLE } from "recoil"

import type { TransactionOperation } from "."
import { addEdge, clearSelection } from "."
import { edgeIndex, findEdgeState } from "../edges"
import { findNodeState, nodeIndex } from "../nodes"
import type { UnknownChangeForUnknownElement } from "../timeline"
import { divergeTimeline, generateElementChange } from "../timeline/transactions"

type IdentifiedNode2d = Node2d & { id: string }

const sortByDistancesFromNode = (
  originNode: IdentifiedNode2d,
  neighboringNodes: IdentifiedNode2d[],
): IdentifiedNode2d[] =>
  neighboringNodes
    .map((node) => {
      const distance = Math.hypot(originNode.x - node.x, originNode.y - node.y)

      return { ...node, distance }
    })
    .sort((a, b) => a.distance - b.distance)

type AddSegmentsOptions = {
  nodes: IdentifiedNode2d[]
  selected: IdentifiedNode2d
  isFirstNode: boolean
  changes: (UnknownChangeForUnknownElement | undefined)[]
}

const addSegmentsBetweenNodes: TransactionOperation<
  AddSegmentsOptions,
  AddSegmentsOptions
> = (transactors, options) => {
  const { get } = transactors
  const { selected, nodes, isFirstNode, changes } = options
  const nodesByDistance = sortByDistancesFromNode(selected, nodes)
  const closestNode = nodesByDistance[0]
  const edges = get(edgeIndex)
  const edgeExists = edges.some(
    (edgeId) => edgeId.includes(closestNode.id) && edgeId.includes(selected.id),
  )

  const edgeAddition =
    !edgeExists && !isFirstNode
      ? addEdge(transactors, {
          id: nameShape(selected.id, closestNode.id),
          payload: { node1id: selected.id, node2id: closestNode.id },
        })
      : undefined

  const newOptions = {
    nodes: nodesByDistance.slice(1),
    selected: closestNode,
    changes: [...changes, edgeAddition],
    isFirstNode: false,
  }

  return nodesByDistance.length > 1
    ? addSegmentsBetweenNodes(transactors, newOptions)
    : newOptions
}

export const eraseNode: TransactionOperation<string> = (
  transactors,
  idToErase,
) => {
  const { get, set } = transactors
  const changes: (UnknownChangeForUnknownElement | undefined)[] = []
  const edgeIds = get(edgeIndex)
  const connectedNodeIds: string[] = []
  for (const edgeId of edgeIds) {
    const { node1id, node2id } = get(findEdgeState(edgeId))
    const [node1Equal, node2Equal] = [
      node1id === idToErase,
      node2id === idToErase,
    ]
    if (node1Equal || node2Equal) {
      const connectedNodeId = node1Equal ? node2id : node1id
      connectedNodeIds.push(connectedNodeId)
    }
  }
  set(nodeIndex, (ids) =>
    ids.filter((curId) => {
      if (idToErase !== curId) {
        return true
      }
      changes.push(
        generateElementChange(transactors, {
          action: {
            type: `DELETE`,
            family: `NODE`,
            id: idToErase,
          },
        }),
      )
    }),
  )
  const erasedNode = { ...get(findNodeState(idToErase)), id: idToErase }

  if (connectedNodeIds.length > 0) {
    set(edgeIndex, (edgeIds) =>
      edgeIds.filter((edgeId) => {
        const shouldDelete = edgeId.includes(idToErase)
        if (!shouldDelete) {
          return true
        }
        changes.push(
          generateElementChange(transactors, {
            action: {
              type: `DELETE`,
              family: `EDGE`,
              id: edgeId,
            },
          }),
        )
      }),
    )
  }

  if (connectedNodeIds.length === 1) {
    const onlyConnectedNodeId = connectedNodeIds[0]
    const edges = get(edgeIndex)
    const edgeExists = edges.some((edgeId) =>
      edgeId.includes(onlyConnectedNodeId),
    )
    if (!edgeExists) {
      set(nodeIndex, (ids) =>
        ids.filter((curId) => {
          if (onlyConnectedNodeId !== curId) {
            return true
          }
          changes.push(
            generateElementChange(transactors, {
              action: {
                type: `DELETE`,
                family: `NODE`,
                id: onlyConnectedNodeId,
              },
            }),
          )
        }),
      )
    }
  }

  if (connectedNodeIds.length > 1) {
    const neighboringNodes: IdentifiedNode2d[] = connectedNodeIds.map(
      (id: string) => {
        const node = get(findNodeState(id))

        return { ...node, id }
      },
    )
    const options: AddSegmentsOptions = {
      nodes: neighboringNodes,
      selected: erasedNode,
      isFirstNode: true,
      changes: [],
    }
    const { changes: segmentAdditions } = addSegmentsBetweenNodes(
      transactors,
      options,
    )
    changes.push(...segmentAdditions)
  }
  divergeTimeline(transactors, changes)
  clearSelection(transactors)
}

export const useEraseNode = (id: string): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => eraseNode(transactors, id),
  )
